<template>
  <StackPageLayout @close="navigateToMoreMenu">
    <template #title>Advance+</template>
    <div
      v-if="businessFundingConfig"
      class="flex flex-col space-y-6"
    >
      <div
        class="flex flex-col items-start space-y-4 rounded-lg bg-background px-4 py-6"
      >
        <IconAppAdvancePlusBig class="h-12 w-auto" />
        <p class="text-3xl font-semibold">Great news!</p>
        <p
          class="text-base font-bold leading-tight text-background-fg/70"
        >
          Your business could be elligible for an advance of
          up to:
        </p>
        <div
          v-if="youlendOffer"
          class="flex w-full items-start justify-center rounded-lg bg-background-fg/5 p-4 text-center text-3xl font-bold text-primary"
        >
          {{
            formatToMerchantCurrencyString(youlendOffer, {
              stripTrailingZeros: true,
            })
          }}<span class="text-lg">*</span>
        </div>
        <p
          class="text-base font-medium leading-tight text-background-fg/70"
        >
          Apply now and you could receive a firm offer from
          our partners within 24 hours.
        </p>
        <ActionButtonPrimary
          class="w-full text-lg font-medium"
          @click="showEmailInputDrawer"
        >
          Apply Now
        </ActionButtonPrimary>
      </div>

      <div
        class="flex flex-col items-start space-y-4 rounded-lg bg-background px-4 py-6"
      >
        <p class="text-2xl font-semibold">
          How does it work?
        </p>
        <p
          class="text-base font-medium leading-tight text-background-fg/70"
        >
          Repayments are based on a fixed percentage of your
          daily card takings. If you've had a mega month,
          you'll pay back a bit more, and if it's been on
          the quieter side, you'll pay back a bit less.
        </p>
        <img
          src="@assets/business-funding/BusinessFundingGraph.png"
          class="mt-4"
        />
        <div>
          <div class="flex items-center">
            <div class="h-0.5 w-4 bg-cash" />
            <div class="size-3 rounded-full bg-cash" />
            <p
              class="ml-2 font-semibold text-background-fg/70"
            >
              Revenue
            </p>
          </div>
          <div class="flex items-center">
            <div
              class="w-4 border border-dashed border-background-fg/60"
            />
            <div
              class="size-3 rounded-full border-2 border-background-fg/60"
            />
            <p
              class="ml-2 font-semibold text-background-fg/70"
            >
              Repayment
            </p>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col items-start space-y-4 rounded-lg bg-background p-4"
      >
        <div class="flex w-full justify-between">
          <p
            class="self-center text-5xl font-bold text-primary"
          >
            {{
              formatToMerchantCurrencyString(
                businessFundingConfig.fundedAmount * 100,
                { compact: true, stripTrailingZeros: true }
              )
            }}+
          </p>
          <img
            src="@assets/business-funding/FundedIcon.png"
            class="size-[4.5rem]"
          />
        </div>
        <p
          class="text-base font-medium leading-tight text-background-fg/70"
        >
          has already been obtained by
          <strong>take</strong>payments customers just like
          you.<sup class="text-xs">†</sup>
        </p>
      </div>

      <div
        class="flex flex-col items-start space-y-4 rounded-lg bg-background p-4"
      >
        <div class="flex w-full justify-between">
          <p
            class="self-center text-5xl font-bold text-cash"
          >
            {{
              formatNumber(
                businessFundingConfig.fundedNumber
              )
            }}+
          </p>
          <img
            src="@assets/business-funding/AdvanceFlexibleIcon.png"
            class="size-[4.5rem]"
          />
        </div>
        <p
          class="text-base font-medium leading-tight text-background-fg/70"
        >
          Over
          {{
            formatNumber(businessFundingConfig.fundedNumber)
          }}
          <strong>take</strong>payments customers have
          benefited from <strong>Advance+</strong> flexbile
          finance so far.<sup class="text-xs">†</sup>
        </p>
      </div>

      <div
        class="flex flex-col items-start space-y-4 rounded-lg bg-background p-4"
      >
        <div
          class="flex w-full items-center justify-between"
        >
          <IconAppAdvancePlusBig class="h-8 w-auto" />
          <img
            src="@assets/business-funding/CustomerServiceIcon.png"
            class="size-[4.5rem]"
          />
        </div>

        <p class="text-2xl font-semibold">
          Have a question?
        </p>

        <p
          class="text-base font-medium leading-tight text-background-fg/70"
        >
          Get in touch with our
          <strong>Advance+</strong> Team and they'll be
          happy to help...
        </p>

        <div>
          <p class="text-3xl font-semibold text-primary">
            {{ businessFundingConfig.contactPhone }}
          </p>

          <p class="text-sm font-medium">
            {{ businessFundingConfig.contactEmail }}
          </p>
        </div>
      </div>

      <div class="px-4 text-xs">
        <p class="font-semibold">
          <sup>†</sup>Terms and Conditions apply
        </p>
        <p class="leading-5">
          Correct as of
          {{
            DateFns.format(
              businessFundingConfig.date,
              "MMMM yyyy"
            )
          }}. takepayments effects introductions to YouLend
          Limited, which is the provider of the Loan Advance
          and Cash Advance product. Funding and the funding
          amount
        </p>
        <p class="mt-2 font-semibold">
          *Terms and Conditions apply
        </p>
        <p class="leading-5">
          The potential funding amount shown is indicative
          only and calculated using only the amount of card
          transactions (via takepayments' terminals or
          facilities) up to a period of 3 months prior to
          {{
            DateFns.format(
              businessFundingConfig.date,
              "MMMM yyyy"
            )
          }}
          and does not consider any other circumstances.
          Businesses must have been trading for 6 months or
          more and be currently open, with a minimum of
          £1,500 in card transactions per month. Other
          eligibility criteria may apply. takepayments is
          acting as an introducer and not a lender.
          takepayments effects introductions to YouLend
          Limited, which is the provider of the Cash Advance
          and Loan Advance product. Funding and the funding
          amount are subject to Youlend's approval.
        </p>
      </div>
    </div>
  </StackPageLayout>
  <BusinessFundingEmailInputDrawer
    :is-open="emailInputDrawerIsOpen"
    @close="hideEmailInputDrawer"
  />
</template>

<script setup lang="ts">
import { useBusinessFundingConfig } from "@/services/retrieveBusinessFundingConfig"
const { formatToMerchantCurrencyString } = useCurrency()
const router = useRouter()

const { youlendOffer } = useTenant()

const navigateToMoreMenu = () => {
  router.push({ name: "moreMenu" })
}

const {
  isVisible: emailInputDrawerIsOpen,
  hideDrawer: hideEmailInputDrawer,
  showDrawer: showEmailInputDrawer,
} = useDrawerState()

const { data: businessFundingConfig } =
  useBusinessFundingConfig()

const formatNumber = (number: number) => {
  const localeBcp47 = "en-GB"
  return new Intl.NumberFormat(localeBcp47, {
    maximumSignificantDigits: 3,
    // notation: "compact",
  }).format(number)
}
</script>
